import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

export interface ManagedUser {
  id: string;
  naam: string | null;
  username: string | null;
  email: string | null;
  functie: string | null;
  role: string | null;
  company: string | null;
  active: boolean | null;
  is_active: boolean | null;
  last_login: string | null;
  afdeling: string | null;
  telefoon: string | null;
  created_at: string | null;
}

export const useUserManagement = () => {
  const queryClient = useQueryClient();

  const { data: users = [], isLoading, refetch } = useQuery({
    queryKey: ["admin-users"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("medewerkers")
        .select("id, naam, username, email, functie, role, company, active, is_active, last_login, afdeling, telefoon, created_at")
        .order("naam");

      if (error) {
        console.error("Error fetching users:", error);
        toast.error("Kon gebruikers niet ophalen");
        return [];
      }

      return (data || []) as ManagedUser[];
    },
  });

  const updateUser = useMutation({
    mutationFn: async (params: { userId: string; updates: Record<string, any> }) => {
      const { error } = await supabase
        .from("medewerkers")
        .update(params.updates)
        .eq("id", params.userId);

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Gebruiker bijgewerkt");
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
    },
    onError: (error: any) => {
      console.error("Error updating user:", error);
      toast.error(`Bijwerken mislukt: ${error.message}`);
    },
  });

  const toggleActive = useMutation({
    mutationFn: async (params: { userId: string; active: boolean }) => {
      const { error } = await supabase
        .from("medewerkers")
        .update({ active: params.active, is_active: params.active })
        .eq("id", params.userId);

      if (error) throw error;
    },
    onSuccess: (_, vars) => {
      toast.success(vars.active ? "Gebruiker geactiveerd" : "Gebruiker gedeactiveerd");
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
    },
    onError: (error: any) => {
      toast.error(`Status wijzigen mislukt: ${error.message}`);
    },
  });

  const resetPassword = async (userId: string, newPassword: string = "temp123"): Promise<boolean> => {
    try {
      const { data, error } = await supabase.rpc("admin_reset_password", {
        p_user_id: userId,
        p_new_password: newPassword,
      });

      if (error) {
        console.error("Password reset error:", error);
        toast.error(`Wachtwoord reset mislukt: ${error.message}`);
        return false;
      }

      if (data) {
        toast.success(`Wachtwoord gereset`);
        return true;
      } else {
        toast.error("Gebruiker niet gevonden");
        return false;
      }
    } catch (err) {
      console.error("Password reset exception:", err);
      toast.error("Er is een fout opgetreden bij het resetten van het wachtwoord");
      return false;
    }
  };

  return {
    users,
    isLoading,
    refetch,
    updateUser,
    toggleActive,
    resetPassword,
  };
};
